import { getLogger } from '@foundation-player/logger';
import { isChromecast, isWebkitEngine } from '@foundation-player/platform-check';
import { EncryptionType, StreamType } from '@foundation-player/runtime';
import { drmProbe, DRMSupport, FAIRPLAY_KEY_SYSTEMS, PLAYREADY_KEY_SYSTEMS, WIDEVINE_KEY_SYSTEMS } from './drm-probe';
import { DASH_MIME_TYPES, HLS_MIME_TYPES, manifestProbe } from './manifest-probe';
import { mediaProbe } from './media-probe';

export interface Capabilities {
  streamTypes: StreamType[];
  encryptionTypes: EncryptionType[];
}

export interface ClientSupport {
  capabilities: Capabilities;
  manifest: Record<string, boolean>;
  media: Record<string, boolean>;
  drm: Record<string, DRMSupport>;
}

/**
 * Run all possible support probes and construct a Capabilities object.
 *
 * @param videoElement - The video element to test with
 */
export async function clientCapabilities(videoElement: HTMLVideoElement): Promise<ClientSupport> {
  const isChromecastReceiver = isChromecast();

  if (isChromecastReceiver) {
    return chromecastCapabilities();
  }

  const logger = getLogger('player.core.capability.clientCapabilities');
  const manifest = manifestProbe(videoElement);
  const media = mediaProbe(videoElement);
  const drm = await drmProbe(videoElement);

  const capabilities: Capabilities = {
    streamTypes: [StreamType.Progressive], // devices can always play StreamType.Progressive
    encryptionTypes: [],
  };

  const nativeDASHSupport = DASH_MIME_TYPES.filter((mimeType) => manifest[mimeType]);
  // Client will always support dash because it's handled by software, as long as MediaSource exists
  if (typeof MediaSource !== 'undefined') {
    logger.debug('Add streamType', {
      streamType: StreamType.DASH,
      nativeSupport: nativeDASHSupport,
    });
    capabilities.streamTypes.push(StreamType.DASH);
  }

  const nativeHLSSupport = HLS_MIME_TYPES.filter((mimeType) => manifest[mimeType]);

  // there are a few devices, especially android phones and tablets, which report HLS manifest support
  // business wise we will only ever support HLS with fairplay
  // because only Apple implements fairplay, and other vendors won't implement support,
  // HLS at RTL will only ever work for webkit devices with HLS support, which should in turn also support fairplay
  // for reference see https://rtltech.atlassian.net/browse/FOUN-2255 and https://rtltech.atlassian.net/browse/FOUN-2259
  // we do not explicitly check fairplay + hls here, because this could force Apple devices into DASH instead of HLS
  // and would also break unencrypted HLS playback for tests
  if (isWebkitEngine() && nativeHLSSupport.length > 0) {
    capabilities.streamTypes.push(StreamType.HLS);
    logger.debug('Add streamType', {
      streamType: StreamType.HLS,
      nativeSupport: nativeHLSSupport,
    });
  }

  const playReadySupport = PLAYREADY_KEY_SYSTEMS.filter((keySystem) => keySystem in drm && drm[keySystem].supported);
  if (playReadySupport.length > 0) {
    capabilities.encryptionTypes.push(EncryptionType.PlayReady);
    logger.debug('Add encryptionType', {
      encryptionType: EncryptionType.PlayReady,
      drmSupport: playReadySupport,
    });
  }

  const widevineSupport = WIDEVINE_KEY_SYSTEMS.filter((keySystem) => keySystem in drm && drm[keySystem].supported);
  if (widevineSupport.length > 0) {
    capabilities.encryptionTypes.push(EncryptionType.Widevine);
    logger.debug('Add encryptionType', {
      encryptionType: EncryptionType.Widevine,
      drmSupport: widevineSupport,
    });
  }

  const fairplaySupport = FAIRPLAY_KEY_SYSTEMS.filter((keySystem) => keySystem in drm && drm[keySystem].supported);
  if (fairplaySupport.length > 0) {
    capabilities.encryptionTypes.push(EncryptionType.FairPlay);
    logger.debug('Add encryptionType', {
      encryptionType: EncryptionType.FairPlay,
      drmSupport: fairplaySupport,
    });
  }

  return {
    capabilities,
    manifest,
    media,
    drm,
  };
}

/**
 * TODO: https://stackoverflow.com/questions/41706028/how-to-detect-chromecast-model-in-receiver
 */
function chromecastCapabilities(): ClientSupport {
  const capabilities: Capabilities = {
    streamTypes: [StreamType.Progressive, StreamType.DASH], // devices can always play StreamType.Progressive
    encryptionTypes: [EncryptionType.Widevine],
  };

  const manifest: Record<string, boolean> = {};
  const media = {};
  const drm = {};
  return { capabilities, manifest, media, drm };
}
